import { useEffect, useState } from 'react';
import axios from 'axios';
import { Pin, X, Megaphone, ChevronDown } from 'lucide-react';

const API = `${window.location.origin}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

/**
 * Pinned announcement hero card for the ambassador dashboard.
 *
 * Renders the most recent **pinned + unread** announcement with a golden
 * glow + dismiss button. Click "dismiss" → calls /read endpoint → fades out.
 * If no pinned unread items, falls back to the latest unread announcement
 * (any kind), still highlighted but without the pinned ribbon.
 *
 * The bell dropdown handles the rest of the feed.
 */
export default function PinnedAnnouncementCard() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/ambassador/me/announcements`, { headers: authHeaders() })
      .then(r => setItems(r.data?.items || []))
      .catch(() => {});
  }, []);

  // Show only unread + not just-dismissed
  const visible = items.filter(a => !a.is_read && !dismissedIds.has(a.id));
  if (visible.length === 0) return null;

  const primary = visible.find(a => a.is_pinned) || visible[0];

  const dismiss = async (id) => {
    setDismissedIds(prev => new Set(prev).add(id));
    try {
      await axios.post(`${API}/ambassador/me/announcements/${id}/read`, {}, { headers: authHeaders() });
    } catch { /* silent — already shown as dismissed locally */ }
  };

  return (
    <div className="mb-6" data-testid="pinned-announcement">
      <div
        className="relative bg-gradient-to-r from-[#1a1408] via-[#241a0a] to-[#1a1408] border border-[#D4AF37]/40 overflow-hidden"
        style={{ boxShadow: '0 0 60px -20px rgba(212, 175, 55, 0.4)' }}
      >
        {/* Decorative golden line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                {primary.is_pinned ? <Pin className="w-4 h-4 text-[#D4AF37]" /> : <Megaphone className="w-4 h-4 text-[#D4AF37]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans font-medium mb-1">
                  {primary.is_pinned ? 'Pinned Note from JS' : 'New from JS'}
                </p>
                <h3 className="text-white text-lg leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {primary.title}
                </h3>
                <p className={`text-white/70 text-sm font-sans mt-2 whitespace-pre-line ${expanded ? '' : 'line-clamp-2'}`}>
                  {primary.body}
                </p>
                {primary.body.length > 140 && (
                  <button onClick={() => setExpanded(!expanded)}
                    className="text-[#D4AF37] text-xs font-sans mt-2 flex items-center gap-1 hover:text-white transition-colors"
                    data-testid="ann-expand-toggle">
                    {expanded ? 'Show less' : 'Read more'} <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() => dismiss(primary.id)}
              className="text-white/40 hover:text-white p-1 flex-shrink-0"
              data-testid="ann-dismiss-btn"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {visible.length > 1 && (
            <p className="text-white/40 text-xs font-sans mt-3 pt-3 border-t border-white/5">
              +{visible.length - 1} more announcement{visible.length - 1 !== 1 ? 's' : ''} — tap the bell to read.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
